/**
 * Управление меню для менеджера/владельца (canManage) — /menu.
 *
 * Артём (2026-06-16): «у менеджера НЕ должно быть „в корзину" — он не
 * создаёт заявку через меню; у менеджера — управление меню: добавлять/
 * убавлять/изменять позиции и категории». Меню — сворачиваемые блоки
 * категорий (аккордеон).
 *
 * Данные: admin-каталог услуг, отфильтрованный до F&B (та же эвристика,
 * что и витрина гостя). CRUD — PUT/DELETE на /v1/admin/service-catalog/*
 * (RBAC бэка: platform_admin|hotel_manager|tenant_owner).
 *
 * Дизайн: согласованный manager v3 (menu admin) — аккордеон категорий,
 * у позиции действия «Изменить»/«Удалить», CTA «+ Позиция»/«+ Категория».
 * i18n: 100% RU. Мобайл 375: одноколоночный stack, формы stacked.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import { formatPriceMinor, localizeText, type PriceMode } from '@hsc/types';
import { Toast } from '../../shared/ui/Toast.js';
import { usePhotoUpload } from '../catalog/usePhotoUpload.js';
import { photoUrl } from '../../lib/fleet-format.js';
import type { AdminCategory, AdminItem, CategoryBody, ItemBody } from './menu-admin.js';
import {
  useMenuAdmin,
  useMenuCategoryMutations,
  useMenuItemMutations,
  type MenuAdminCategory,
} from './useMenu.js';

const LOCALE = 'ru';
const DEFAULT_CURRENCY = 'RUB';

/** Маржинальный порог: себестоимость > 40% цены → визуальный варн (§9.3). */
const MARGIN_WARN_RATIO = 0.4;

/**
 * Канонический словарь тегов аллергенов/диет (§9.3 / §9.4). Совпадает с
 * белым списком бэка (domain.allowedAllergens) — иначе тег молча отбросится.
 */
const ALLERGEN_TAGS: readonly { key: string; label: string; tone: 'ok' | 'warn' | 'bad' }[] = [
  { key: 'veg', label: 'veg', tone: 'ok' },
  { key: 'vegan', label: 'vegan', tone: 'ok' },
  { key: 'gluten', label: 'gluten', tone: 'warn' },
  { key: 'lactose', label: 'lactose', tone: 'warn' },
  { key: 'nuts', label: 'nuts', tone: 'warn' },
  { key: 'spicy', label: 'spicy', tone: 'bad' },
];

const ALLERGEN_TONE = new Map(ALLERGEN_TAGS.map((t) => [t.key, t.tone] as const));

/** Себестоимость позиции (RU-строка) или null, если не указана. */
function costLabel(item: AdminItem): string | null {
  if (item.cost_minor === undefined || item.cost_minor === null) return null;
  return formatPriceMinor(item.cost_minor, item.currency ?? DEFAULT_CURRENCY, LOCALE);
}

/** true → себестоимость превышает 40% цены (margin-warning §9.3). */
function isLowMargin(item: AdminItem): boolean {
  if (
    item.cost_minor === undefined ||
    item.cost_minor === null ||
    item.price_minor === undefined ||
    item.price_minor <= 0
  ) {
    return false;
  }
  return item.cost_minor > item.price_minor * MARGIN_WARN_RATIO;
}

/** Слаг из RU-названия для кода новой категории/позиции (a-z0-9_, латиница). */
function slugify(input: string, prefix: string): string {
  const map: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
    и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
    с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
    ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };
  const base = input
    .toLowerCase()
    .split('')
    .map((ch) => map[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  const body = base.length >= 3 ? base : `${prefix}_${base}`;
  // code CHECK ^[a-z][a-z0-9_]{2,30}$ — гарантируем буквенное начало + длину.
  const safe = /^[a-z]/.test(body) ? body : `${prefix}_${body}`;
  return `${safe}_${Date.now().toString(36).slice(-4)}`.slice(0, 30);
}

/** RU-строка цены позиции для строки управления (без витринных «от …»). */
function priceLabel(item: AdminItem): string {
  if (!item.is_paid || item.price_mode === 'none' || item.price_minor === undefined) {
    return 'Бесплатно';
  }
  return formatPriceMinor(item.price_minor, item.currency ?? DEFAULT_CURRENCY, LOCALE);
}

export interface MenuManagerProps {
  canManage: boolean;
}

type Editing =
  | { kind: 'category'; category: AdminCategory | null }
  | { kind: 'item'; categoryId: string; item: AdminItem | null }
  | null;

export function MenuManager(_props: MenuManagerProps): JSX.Element {
  const navigate = useNavigate();
  const { data: categories, isLoading, isError, refetch } = useMenuAdmin();
  const itemMut = useMenuItemMutations();
  const [editing, setEditing] = useState<Editing>(null);
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);
  const notify = (msg: string, tone: 'info' | 'error' = 'info'): void => setToast({ msg, tone });

  // Фильтры (§9.3): категория, статус, аллергены (мультивыбор), поиск.
  const [catFilter, setCatFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');
  const [allergenFilter, setAllergenFilter] = useState<readonly string[]>([]);
  const [search, setSearch] = useState('');
  // Bulk-выбор позиций.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Плоский список позиций (категория известна для каждой строки).
  const rows = useMemo(
    () =>
      (categories ?? []).flatMap((c) =>
        c.items.map((it) => ({ item: it, catName: localizeText(c.name, LOCALE) || c.code, catId: c.id })),
      ),
    [categories],
  );
  const totalItems = rows.length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(({ item }) => {
      if (catFilter !== 'all' && item.category_id !== catFilter) return false;
      if (statusFilter === 'active' && !item.is_active) return false;
      if (statusFilter === 'archived' && item.is_active) return false;
      if (allergenFilter.length > 0) {
        const tags = item.allergens ?? [];
        if (!allergenFilter.every((a) => tags.includes(a))) return false;
      }
      if (q !== '' && !(localizeText(item.name, LOCALE) || item.code).toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rows, catFilter, statusFilter, allergenFilter, search]);

  const toggleAllergen = (key: string): void =>
    setAllergenFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  const toggleSelect = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = (): void => setSelected(new Set());
  const selectedItems = useMemo(
    () => filtered.filter((r) => selected.has(r.item.id)).map((r) => r.item),
    [filtered, selected],
  );

  const editItem = (catId: string, item: AdminItem | null): void =>
    setEditing({ kind: 'item', categoryId: catId, item });

  // --- форма-экраны (отдельный экран с «Назад», правило BINDING) ----------
  if (editing?.kind === 'category') {
    return (
      <CategoryForm
        category={editing.category}
        onClose={() => setEditing(null)}
        onDone={(m) => {
          setEditing(null);
          notify(m);
          void refetch();
        }}
        onError={(m) => notify(m, 'error')}
      />
    );
  }
  if (editing?.kind === 'item') {
    const cat = categories?.find((c) => c.id === editing.categoryId);
    const catName = cat ? localizeText(cat.name, LOCALE) || cat.code : '';
    return (
      <ItemForm
        categoryId={editing.categoryId}
        categoryName={catName}
        categories={categories ?? []}
        item={editing.item}
        onClose={() => setEditing(null)}
        onDone={(m) => {
          setEditing(null);
          notify(m);
          void refetch();
        }}
        onError={(m) => notify(m, 'error')}
      />
    );
  }

  const firstCatId = categories?.[0]?.id;

  return (
    <section data-testid="menu-manager">
      <button
        type="button"
        className="ta-back-btn"
        data-testid="menu-items-back"
        onClick={() => navigate('/menu')}
      >
        <span className="ta-back-btn__chev" aria-hidden="true">
          ‹
        </span>{' '}
        Назад
      </button>
      <div className="mi-topbar">
        <div className="mi-topbar__lead">
          <div className="mi-crumbs">Меню / Позиции</div>
          <h1 className="hsc-page__title mi-title">Позиции · {totalItems}</h1>
        </div>
        <div className="mi-topbar__actions">
          <Button
            variant="ghost"
            size="sm"
            data-testid="menu-import-csv"
            onClick={() => notify('Импорт CSV скоро будет доступен.')}
          >
            Импорт CSV
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="menu-add-item"
            disabled={firstCatId === undefined}
            onClick={() => firstCatId !== undefined && editItem(firstCatId, null)}
          >
            + Позиция
          </Button>
        </div>
      </div>

      {/* Фильтр-чипы §9.3: категория ▾ · статус ▾ · veg · gluten-free · spicy + поиск */}
      <div className="mi-filters" data-testid="menu-filters">
        <select
          className="ta-select mi-filter-select"
          aria-label="Категория"
          data-testid="menu-filter-category"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
        >
          <option value="all">Категория: все</option>
          {(categories ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {localizeText(c.name, LOCALE) || c.code}
            </option>
          ))}
        </select>
        <select
          className="ta-select mi-filter-select"
          aria-label="Статус"
          data-testid="menu-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'archived')}
        >
          <option value="all">Статус: все</option>
          <option value="active">активна</option>
          <option value="archived">в архиве</option>
        </select>
        {ALLERGEN_TAGS.filter((t) => ['veg', 'gluten', 'spicy'].includes(t.key)).map((t) => (
          <button
            type="button"
            key={t.key}
            className={`ta-chip mi-chip-filter${allergenFilter.includes(t.key) ? ' is-on' : ''}`}
            aria-pressed={allergenFilter.includes(t.key)}
            data-testid={`menu-filter-allergen-${t.key}`}
            onClick={() => toggleAllergen(t.key)}
          >
            {t.key === 'gluten' ? 'gluten-free' : t.label}
          </button>
        ))}
        <input
          type="search"
          className="ta-input mi-search"
          placeholder="Поиск…"
          aria-label="Поиск по названию"
          data-testid="menu-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Bulk-панель §9.3 */}
      {selected.size > 0 && (
        <BulkBar
          items={selectedItems}
          mut={itemMut}
          onDone={(m) => {
            notify(m);
            clearSelection();
            void refetch();
          }}
          onError={(m) => notify(m, 'error')}
          onClear={clearSelection}
        />
      )}

      {isLoading ? (
        <div data-testid="menu-manager-skeleton" className="mi-skel">
          {Array.from({ length: 10 }, (_, i) => (
            <Skeleton key={i} height={44} radius={8} />
          ))}
        </div>
      ) : isError || categories === undefined ? (
        <Card>
          <p role="alert" data-testid="menu-manager-error">
            Не удалось загрузить меню. Попробуйте обновить страницу.
          </p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
          </div>
        </Card>
      ) : totalItems === 0 ? (
        <Card>
          <p data-testid="menu-manager-empty">Здесь будут позиции меню. Создайте первую.</p>
          {firstCatId !== undefined && (
            <div className="rq-actions">
              <Button variant="primary" onClick={() => editItem(firstCatId, null)}>
                + Позиция
              </Button>
            </div>
          )}
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <p data-testid="menu-filter-empty">По выбранным фильтрам ничего нет.</p>
        </Card>
      ) : (
        <ItemsTable
          rows={filtered}
          selected={selected}
          onToggle={toggleSelect}
          onOpen={(catId, item) => editItem(catId, item)}
        />
      )}

      {toast && <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />}
    </section>
  );
}

/** Чипы аллергенов позиции (для таблицы/карточки). */
function AllergenChips({ item }: { item: AdminItem }): JSX.Element {
  const tags = item.allergens ?? [];
  if (tags.length === 0) return <span className="mi-dash">—</span>;
  return (
    <span className="mi-allergens" data-testid={`menu-item-${item.code}-allergens`}>
      {tags.map((t) => (
        <span key={t} className={`ta-chip mi-aller mi-aller--${ALLERGEN_TONE.get(t) ?? 'ok'}`}>
          {t}
        </span>
      ))}
    </span>
  );
}

/** Статус-чип позиции (активна / в архиве). */
function StatusChip({ active }: { active: boolean }): JSX.Element {
  return active ? (
    <span className="ta-chip ta-chip--ok">активна</span>
  ) : (
    <span className="ta-chip">в архиве</span>
  );
}

type Row = { item: AdminItem; catName: string; catId: string };

/** Таблица позиций §9.3 (1280 полная · 1024 без «Себестоимость» · 375 карточки). */
function ItemsTable({
  rows,
  selected,
  onToggle,
  onOpen,
}: {
  rows: readonly Row[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onOpen: (catId: string, item: AdminItem) => void;
}): JSX.Element {
  return (
    <table className="mi-table" data-testid="menu-items-table">
      <thead>
        <tr>
          <th className="mi-th-check" aria-label="Выбор" />
          <th className="mi-th-thumb" aria-label="Фото" />
          <th>Название</th>
          <th>Категория</th>
          <th className="mi-num">Цена</th>
          <th className="mi-num mi-col-cost">Себестоимость</th>
          <th>Allergens</th>
          <th>Статус</th>
          <th aria-label="Открыть" />
        </tr>
      </thead>
      <tbody>
        {rows.map(({ item, catName, catId }) => {
          const cost = costLabel(item);
          const low = isLowMargin(item);
          return (
            <tr
              key={item.id}
              className={`mi-row${selected.has(item.id) ? ' is-selected' : ''}`}
              data-testid={`menu-item-row-${item.code}`}
            >
              <td className="mi-td-check">
                <input
                  type="checkbox"
                  aria-label={`Выбрать ${localizeText(item.name, LOCALE) || item.code}`}
                  data-testid={`menu-item-select-${item.code}`}
                  checked={selected.has(item.id)}
                  onChange={() => onToggle(item.id)}
                />
              </td>
              <td>
                <ItemThumb item={item} />
              </td>
              <td>
                <button
                  type="button"
                  className="mi-name-btn"
                  data-testid={`menu-item-open-${item.code}`}
                  onClick={() => onOpen(catId, item)}
                >
                  {localizeText(item.name, LOCALE) || item.code}
                </button>
              </td>
              <td className="mi-cat">{catName}</td>
              <td className="mi-num mi-price">{priceLabel(item)}</td>
              <td className={`mi-num mi-col-cost${low ? ' mi-cost--warn' : ''}`}>
                {cost === null ? (
                  <span className="mi-dash">—</span>
                ) : (
                  <span
                    data-testid={`menu-item-${item.code}-cost`}
                    title={low ? 'Себестоимость выше 40% цены' : undefined}
                  >
                    {cost}
                    {low && (
                      <span className="mi-warn-mark" aria-label="Низкая маржа" data-testid={`menu-item-${item.code}-margin-warn`}>
                        {' '}
                        ⚠
                      </span>
                    )}
                  </span>
                )}
              </td>
              <td>
                <AllergenChips item={item} />
              </td>
              <td>
                <StatusChip active={item.is_active} />
              </td>
              <td className="mi-arrow">
                <button
                  type="button"
                  className="mi-arrow-btn"
                  aria-label="Открыть позицию"
                  data-testid={`menu-item-arrow-${item.code}`}
                  onClick={() => onOpen(catId, item)}
                >
                  →
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Миниатюра позиции 32×32 (фото или плейсхолдер). */
function ItemThumb({ item }: { item: AdminItem }): JSX.Element {
  const src =
    item.image_preview_url !== undefined && item.image_preview_url !== ''
      ? item.image_preview_url
      : item.image_url !== undefined && item.image_url !== ''
        ? (photoUrl(item.image_url) ?? item.image_url)
        : null;
  if (src === null) return <span className="mi-thumb mi-thumb--ph" aria-hidden="true" />;
  return <img className="mi-thumb" src={src} alt="" loading="lazy" />;
}

/** Bulk-панель: «Изменить цену %» / «Архивировать» / «Снять выбор». */
function BulkBar({
  items,
  mut,
  onDone,
  onError,
  onClear,
}: {
  items: AdminItem[];
  mut: ReturnType<typeof useMenuItemMutations>;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
  onClear: () => void;
}): JSX.Element {
  const [percent, setPercent] = useState('10');
  const busy = mut.archiveMany.isPending || mut.repriceMany.isPending;

  const reprice = (): void => {
    const p = Number(percent);
    if (Number.isNaN(p) || p === 0) {
      onError('Укажите ненулевой процент (например 10 или -15).');
      return;
    }
    mut.repriceMany.mutate(
      { items, percent: p },
      {
        onSuccess: () => onDone(`Цена изменена на ${p > 0 ? '+' : ''}${p}% для выбранных.`),
        onError: (e) => onError(e.message || 'Не удалось изменить цену.'),
      },
    );
  };
  const archive = (): void => {
    mut.archiveMany.mutate(
      items.map((i) => i.id),
      {
        onSuccess: () => onDone('Выбранные позиции архивированы.'),
        onError: (e) => onError(e.message || 'Не удалось архивировать.'),
      },
    );
  };

  return (
    <div className="mi-bulk" data-testid="menu-bulk-bar">
      <span className="mi-bulk__count" data-testid="menu-bulk-count">
        Выбрано: {items.length}
      </span>
      <span className="mi-bulk__reprice">
        <label htmlFor="mi-bulk-pct">Цена&nbsp;%</label>
        <input
          id="mi-bulk-pct"
          type="number"
          className="ta-input mi-bulk__pct"
          step="1"
          value={percent}
          data-testid="menu-bulk-percent"
          onChange={(e) => setPercent(e.target.value)}
        />
        <Button variant="secondary" size="sm" disabled={busy} data-testid="menu-bulk-reprice" onClick={reprice}>
          Изменить цену %
        </Button>
      </span>
      <Button variant="ghost" size="sm" disabled={busy} data-testid="menu-bulk-archive" onClick={archive}>
        Архивировать
      </Button>
      <Button variant="ghost" size="sm" disabled={busy} data-testid="menu-bulk-clear" onClick={onClear}>
        Снять выбор
      </Button>
    </div>
  );
}

const EMPTY_CATEGORY = {
  name: '',
  description: '',
  department_code: 'f_and_b',
  icon: '',
  availabilityMode: 'all_day' as 'all_day' | 'custom',
  availFrom: '07:00',
  availTo: '23:00',
  sort_order: 100,
};

/** Набор иконок категории (Phosphor-имена) для пикера 9.2. */
const CATEGORY_ICONS: readonly { key: string; glyph: string; title: string }[] = [
  { key: 'coffee', glyph: '☕', title: 'Кофе / Завтраки' },
  { key: 'fork-knife', glyph: '🍴', title: 'Горячее' },
  { key: 'salad', glyph: '🥗', title: 'Салаты' },
  { key: 'cake', glyph: '🍰', title: 'Десерты' },
  { key: 'wine', glyph: '🍷', title: 'Бар' },
  { key: 'beer', glyph: '🍺', title: 'Напитки' },
  { key: 'baby', glyph: '🧸', title: 'Детское' },
  { key: 'leaf', glyph: '🌱', title: 'Веганское' },
];

/** Разбирает "HH:MM-HH:MM" → {from,to}; пусто → дефолт окна. */
function parseAvailability(v: string | undefined): { from: string; to: string } {
  const m = (v ?? '').match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  return m ? { from: m[1] as string, to: m[2] as string } : { from: '07:00', to: '23:00' };
}

/** Форма создания/редактирования категории меню. */
export function CategoryForm({
  category,
  onClose,
  onDone,
  onError,
}: {
  category: AdminCategory | null;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const isEdit = category !== null;
  const { create, update, remove, setActive } = useMenuCategoryMutations();
  // Чекбокс «Активна» / архив (§9.2, #74): на создание — по умолчанию активна.
  const [isActive, setIsActive] = useState<boolean>(category ? category.is_active : true);
  const [form, setForm] = useState(() =>
    category
      ? {
          name: localizeText(category.name, LOCALE),
          description: localizeText(category.description, LOCALE),
          department_code: category.department_code || 'f_and_b',
          icon: category.icon ?? '',
          availabilityMode: (category.availability ? 'custom' : 'all_day') as 'all_day' | 'custom',
          availFrom: parseAvailability(category.availability).from,
          availTo: parseAvailability(category.availability).to,
          sort_order: category.sort_order,
        }
      : { ...EMPTY_CATEGORY },
  );
  // Обложка категории (cover_url): presign-загрузка как у фото позиции/номера.
  const cover = usePhotoUpload(1);
  const [existingCover, setExistingCover] = useState<string | null>(
    category && category.cover_url !== undefined && category.cover_url !== ''
      ? category.cover_url
      : null,
  );
  const newCover = cover.photos[0];
  const coverPreview =
    newCover?.previewUrl !== undefined && newCover.previewUrl !== ''
      ? newCover.previewUrl
      : existingCover !== null
        ? photoUrl(existingCover) ?? existingCover
        : null;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const busy =
    create.isPending ||
    update.isPending ||
    remove.isPending ||
    setActive.isPending ||
    cover.isUploading;
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]): void =>
    setForm((f) => ({ ...f, [k]: v }));

  const resolveCover = (): string => {
    if (cover.uploadedKeys.length > 0) return cover.uploadedKeys[0] ?? '';
    if (existingCover !== null) return existingCover;
    return '';
  };
  const removeCover = (): void => {
    if (newCover !== undefined) cover.removePhoto(newCover.id);
    setExistingCover(null);
  };

  const submit = (): void => {
    if (form.name.trim() === '') {
      onError('Укажите название категории.');
      return;
    }
    if (cover.isUploading) {
      onError('Дождитесь загрузки обложки.');
      return;
    }
    const availability =
      form.availabilityMode === 'custom' ? `${form.availFrom}-${form.availTo}` : '';
    const body: CategoryBody = {
      name: { ru: form.name.trim() },
      description: form.description.trim() !== '' ? { ru: form.description.trim() } : {},
      department_code: form.department_code,
      icon: form.icon,
      cover_url: resolveCover(),
      availability,
      sort_order: form.sort_order,
    };
    if (isEdit) {
      const activeChanged = isActive !== category.is_active;
      update.mutate(
        { id: category.id, body },
        {
          onSuccess: () => {
            // Статус «Активна»/архив правится отдельным эндпойнтом
            // activate/deactivate — применяем только если изменился.
            if (activeChanged) {
              setActive.mutate(
                { id: category.id, active: isActive },
                {
                  onSuccess: () =>
                    onDone(isActive ? 'Категория сохранена.' : 'Категория сохранена и убрана в архив.'),
                  onError: (e) => onError(e.message || 'Не удалось изменить статус категории.'),
                },
              );
            } else {
              onDone('Категория сохранена.');
            }
          },
          onError: (e) => onError(e.message || 'Не удалось сохранить категорию.'),
        },
      );
    } else {
      create.mutate(
        { ...body, code: slugify(form.name, 'cat') },
        {
          onSuccess: () => onDone('Категория добавлена.'),
          onError: (e) => onError(e.message || 'Не удалось создать категорию.'),
        },
      );
    }
  };

  const del = (): void => {
    if (!category) return;
    remove.mutate(category.id, {
      onSuccess: () => onDone('Категория удалена.'),
      onError: (e) => onError(e.message || 'Не удалось удалить категорию.'),
    });
  };

  return (
    <section className="menu-form" data-testid="menu-category-form">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">{isEdit ? 'Изменить категорию' : 'Новая категория'}</h1>
      </div>
      <Card>
        <div className="ta-field">
          <label htmlFor="mc-name">Название</label>
          <input
            id="mc-name"
            className="ta-input"
            type="text"
            maxLength={120}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Например: Завтраки"
            data-testid="menu-cat-name"
          />
        </div>
        <div className="ta-field">
          <label htmlFor="mc-desc">Описание</label>
          <textarea
            id="mc-desc"
            className="ta-textarea"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Необязательно"
            data-testid="menu-cat-desc"
          />
        </div>

        {/* Иконка категории (design §9.2) — пикер набора иконок. */}
        <div className="ta-field">
          <span className="hsc-input__label">Иконка</span>
          <div className="menu-icon-picker" role="group" aria-label="Иконка категории">
            {CATEGORY_ICONS.map((ic) => (
              <button
                type="button"
                key={ic.key}
                title={ic.title}
                aria-pressed={form.icon === ic.key}
                className={`menu-icon-opt${form.icon === ic.key ? ' menu-icon-opt--on' : ''}`}
                data-testid={`menu-cat-icon-${ic.key}`}
                onClick={() => set('icon', form.icon === ic.key ? '' : ic.key)}
              >
                <span aria-hidden="true">{ic.glyph}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Фото-обложка категории (cover_url) — presign-загрузка. */}
        <div className="ta-field">
          <span className="hsc-input__label" id="mc-cover-label">
            Фото-обложка
          </span>
          <div className="rq-photos" role="group" aria-labelledby="mc-cover-label">
            {coverPreview !== null ? (
              <span className="rq-photo rq-photo--done" data-testid="menu-cat-cover-preview">
                <img className="rq-photo__img" src={coverPreview} alt="Обложка категории" />
                <button
                  type="button"
                  className="rq-photo__remove"
                  aria-label="Убрать обложку"
                  data-testid="menu-cat-cover-remove"
                  onClick={removeCover}
                >
                  ✕
                </button>
              </span>
            ) : (
              <label className="rq-photo-add" aria-label="Добавить обложку">
                <span className="rq-photo-add__plus" aria-hidden="true">
                  +
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="rq-photo-add__input"
                  data-testid="menu-cat-cover-input"
                  onChange={(e) => cover.addFiles(e.target.files)}
                />
              </label>
            )}
          </div>
        </div>

        {/* Время доступности (design §9.2 / §9.4 pin): по умолчанию весь день. */}
        <div className="ta-field">
          <span className="hsc-input__label">Время доступности</span>
          <div className="menu-avail-modes">
            <button
              type="button"
              className={`chip${form.availabilityMode === 'all_day' ? ' chip--on' : ''}`}
              aria-pressed={form.availabilityMode === 'all_day'}
              data-testid="menu-cat-avail-allday"
              onClick={() => set('availabilityMode', 'all_day')}
            >
              Весь день (часы ресторана)
            </button>
            <button
              type="button"
              className={`chip${form.availabilityMode === 'custom' ? ' chip--on' : ''}`}
              aria-pressed={form.availabilityMode === 'custom'}
              data-testid="menu-cat-avail-custom"
              onClick={() => set('availabilityMode', 'custom')}
            >
              Своё окно
            </button>
          </div>
          {form.availabilityMode === 'custom' && (
            <div className="menu-avail-window" data-testid="menu-cat-avail-window">
              <label>
                С
                <input
                  type="time"
                  className="ta-input"
                  value={form.availFrom}
                  onChange={(e) => set('availFrom', e.target.value)}
                  data-testid="menu-cat-avail-from"
                />
              </label>
              <label>
                по
                <input
                  type="time"
                  className="ta-input"
                  value={form.availTo}
                  onChange={(e) => set('availTo', e.target.value)}
                  data-testid="menu-cat-avail-to"
                />
              </label>
            </div>
          )}
        </div>

        <div className="ta-field">
          <label htmlFor="mc-sort">Порядок сортировки</label>
          <input
            id="mc-sort"
            className="ta-input"
            type="number"
            value={form.sort_order}
            onChange={(e) => set('sort_order', Number(e.target.value) || 0)}
            data-testid="menu-cat-sort"
          />
        </div>

        {/* Статус категории «Активна» / архив (§9.2, #74): снятый чекбокс
            убирает категорию из меню (deactivate), не удаляя её. */}
        <label className="menu-form__check">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid="menu-cat-active"
          />
          Активна (показывать в меню)
        </label>
      </Card>

      <div className="menu-form__submit">
        <Button variant="primary" disabled={busy} onClick={submit} data-testid="menu-cat-submit">
          {isEdit ? 'Сохранить' : 'Добавить категорию'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onClose} data-testid="menu-cat-form-close">
          Назад
        </Button>
        {isEdit && !category.is_system && (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
            data-testid="menu-cat-delete"
          >
            Удалить категорию
          </Button>
        )}
      </div>

      {confirmDelete && (
        <Card>
          <p>Удалить категорию «{form.name}» со всеми позициями? Действие необратимо.</p>
          <div className="menu-form__submit">
            <Button variant="primary" disabled={busy} onClick={del} data-testid="menu-cat-delete-confirm">
              Удалить
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Отмена
            </Button>
          </div>
        </Card>
      )}
    </section>
  );
}

const EMPTY_ITEM = {
  name: '',
  description: '',
  is_paid: true,
  price_major: '',
  cost_major: '',
  show_price: true,
  sort_order: 100,
};

/** Форма создания/редактирования позиции меню (§9.4 — отдельный экран). */
function ItemForm({
  categoryId,
  categoryName,
  categories,
  item,
  onClose,
  onDone,
  onError,
}: {
  categoryId: string;
  categoryName: string;
  categories: readonly MenuAdminCategory[];
  item: AdminItem | null;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const isEdit = item !== null;
  const { create, update, setActive } = useMenuItemMutations();
  // Чекбокс «Активна» / архив позиции (§9.3/§9.4, #74). На создание — активна.
  const [isActive, setIsActive] = useState<boolean>(item ? item.is_active : true);
  // Выбранная категория (на создание можно сменить; на редактирование
  // category_id у позиции immutable, поэтому селект только при создании).
  const [targetCatId, setTargetCatId] = useState<string>(item?.category_id ?? categoryId);
  // Теги аллергенов/диет позиции.
  const [allergens, setAllergens] = useState<readonly string[]>(item?.allergens ?? []);
  const [form, setForm] = useState(() =>
    item
      ? {
          name: localizeText(item.name, LOCALE),
          description: localizeText(item.description, LOCALE),
          is_paid: item.is_paid,
          price_major:
            item.price_minor !== undefined ? String(item.price_minor / 100) : '',
          cost_major:
            item.cost_minor !== undefined && item.cost_minor !== null
              ? String(item.cost_minor / 100)
              : '',
          show_price: item.show_price,
          sort_order: item.sort_order,
        }
      : { ...EMPTY_ITEM },
  );
  const toggleAllergen = (key: string): void =>
    setAllergens((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  // Фото позиции (Волна 5 / B1): загрузка через presign /v1/files. Существующее
  // фото показываем превью-URL'ом, новое — заменяет его; «Убрать» очищает.
  const photo = usePhotoUpload(1);
  const [existingImage, setExistingImage] = useState<{ url: string; preview: string } | null>(
    item && item.image_url !== undefined && item.image_url !== ''
      ? { url: item.image_url, preview: item.image_preview_url ?? item.image_url }
      : null,
  );
  const newPhoto = photo.photos[0];
  const previewSrc =
    newPhoto?.previewUrl !== undefined && newPhoto.previewUrl !== ''
      ? newPhoto.previewUrl
      : (existingImage?.preview ?? null);
  const busy = create.isPending || update.isPending || setActive.isPending || photo.isUploading;
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]): void =>
    setForm((f) => ({ ...f, [k]: v }));

  /** Итоговое значение image_url для тела запроса (см. правила выше). */
  const resolveImageUrl = (): string | null => {
    if (photo.uploadedKeys.length > 0) return photo.uploadedKeys[0] ?? null;
    if (existingImage !== null) return existingImage.url;
    return null;
  };

  const removePhoto = (): void => {
    if (newPhoto !== undefined) photo.removePhoto(newPhoto.id);
    setExistingImage(null);
  };

  const submit = (): void => {
    if (form.name.trim() === '') {
      onError('Укажите название позиции.');
      return;
    }
    if (photo.isUploading) {
      onError('Дождитесь загрузки фото.');
      return;
    }
    const priceNum = form.price_major.trim() === '' ? null : Number(form.price_major);
    if (form.is_paid && (priceNum === null || Number.isNaN(priceNum) || priceNum < 0)) {
      onError('Укажите корректную цену (или снимите «платная»).');
      return;
    }
    const costNum = form.cost_major.trim() === '' ? null : Number(form.cost_major);
    if (costNum !== null && (Number.isNaN(costNum) || costNum < 0)) {
      onError('Себестоимость должна быть неотрицательным числом (или пустой).');
      return;
    }
    const priceMode: PriceMode = form.is_paid && priceNum !== null ? 'fixed' : 'none';
    const body: ItemBody = {
      name: { ru: form.name.trim() },
      description: form.description.trim() !== '' ? { ru: form.description.trim() } : {},
      is_paid: form.is_paid,
      price_mode: priceMode,
      price_minor: priceMode === 'fixed' && priceNum !== null ? Math.round(priceNum * 100) : null,
      currency: priceMode === 'fixed' ? DEFAULT_CURRENCY : null,
      show_price: form.show_price,
      image_url: resolveImageUrl(),
      cost_minor: costNum !== null ? Math.round(costNum * 100) : null,
      allergens: [...allergens],
      sort_order: form.sort_order,
    };
    if (isEdit) {
      const activeChanged = isActive !== item.is_active;
      update.mutate(
        { id: item.id, body },
        {
          onSuccess: () => {
            if (activeChanged) {
              setActive.mutate(
                { id: item.id, active: isActive },
                {
                  onSuccess: () =>
                    onDone(isActive ? 'Позиция сохранена.' : 'Позиция сохранена и убрана в архив.'),
                  onError: (e) => onError(e.message || 'Не удалось изменить статус позиции.'),
                },
              );
            } else {
              onDone('Позиция сохранена.');
            }
          },
          onError: (e) => onError(e.message || 'Не удалось сохранить позицию.'),
        },
      );
    } else {
      create.mutate(
        { ...body, category_id: targetCatId, code: slugify(form.name, 'item') },
        {
          onSuccess: () => onDone('Позиция добавлена в меню.'),
          onError: (e) => onError(e.message || 'Не удалось создать позицию.'),
        },
      );
    }
  };

  return (
    <section className="menu-form" data-testid="menu-item-form">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">{isEdit ? 'Изменить позицию' : 'Новая позиция'}</h1>
      </div>
      {isEdit ? (
        <p className="ta-muted" data-testid="menu-item-form-cat">
          Категория: <strong>{categoryName}</strong>
        </p>
      ) : null}
      <Card>
        {!isEdit && categories.length > 0 && (
          <div className="ta-field">
            <label htmlFor="mi-category">Категория</label>
            <select
              id="mi-category"
              className="ta-select"
              value={targetCatId}
              onChange={(e) => setTargetCatId(e.target.value)}
              data-testid="menu-item-category"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {localizeText(c.name, LOCALE) || c.code}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="ta-field">
          <label htmlFor="mi-name">Название</label>
          <input
            id="mi-name"
            className="ta-input"
            type="text"
            maxLength={160}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Например: Сырники со сметаной"
            data-testid="menu-item-name"
          />
        </div>
        <div className="ta-field">
          <label htmlFor="mi-desc">Описание</label>
          <textarea
            id="mi-desc"
            className="ta-textarea"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Состав, граммовка — необязательно"
            data-testid="menu-item-desc"
          />
        </div>
        <div className="ta-field">
          <span className="hsc-input__label" id="mi-photo-label">
            Фото позиции
          </span>
          <div className="rq-photos" role="group" aria-labelledby="mi-photo-label">
            {previewSrc !== null && (
              <span className="rq-photo rq-photo--done" data-testid="menu-item-photo-preview">
                <img className="rq-photo__img" src={previewSrc} alt="Фото позиции" />
                <button
                  type="button"
                  className="rq-photo__remove"
                  aria-label="Убрать фото"
                  data-testid="menu-item-photo-remove"
                  onClick={removePhoto}
                >
                  ✕
                </button>
              </span>
            )}
            {previewSrc === null && (
              <label className="rq-photo-add" aria-label="Добавить фото">
                <span className="rq-photo-add__plus" aria-hidden="true">
                  +
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="rq-photo-add__input"
                  data-testid="menu-item-photo-input"
                  onChange={(e) => photo.addFiles(e.target.files)}
                />
              </label>
            )}
          </div>
          {newPhoto?.status === 'error' && (
            <p className="rq-photo-error" role="alert" data-testid="menu-item-photo-error">
              Не удалось загрузить фото. Попробуйте ещё раз.
            </p>
          )}
        </div>
        <label className="menu-form__check">
          <input
            type="checkbox"
            checked={form.is_paid}
            onChange={(e) => set('is_paid', e.target.checked)}
            data-testid="menu-item-paid"
          />
          Платная позиция
        </label>
        {form.is_paid && (
          <div className="ta-field">
            <label htmlFor="mi-price">Цена, ₽</label>
            <input
              id="mi-price"
              className="ta-input"
              type="number"
              min={0}
              step="1"
              value={form.price_major}
              onChange={(e) => set('price_major', e.target.value)}
              placeholder="450"
              data-testid="menu-item-price"
            />
          </div>
        )}
        {/* Себестоимость (§9.3/§9.4): для маржи. Необязательна. */}
        <div className="ta-field">
          <label htmlFor="mi-cost">Себестоимость, ₽</label>
          <input
            id="mi-cost"
            className="ta-input"
            type="number"
            min={0}
            step="1"
            value={form.cost_major}
            onChange={(e) => set('cost_major', e.target.value)}
            placeholder="Например: 180 (необязательно)"
            data-testid="menu-item-cost"
          />
          {(() => {
            const p = Number(form.price_major);
            const c = Number(form.cost_major);
            if (
              form.is_paid &&
              form.cost_major.trim() !== '' &&
              form.price_major.trim() !== '' &&
              !Number.isNaN(p) &&
              p > 0 &&
              !Number.isNaN(c) &&
              c > p * MARGIN_WARN_RATIO
            ) {
              return (
                <span className="mi-cost-hint" data-testid="menu-item-cost-warn">
                  ⚠ Себестоимость выше 40% цены — низкая маржа.
                </span>
              );
            }
            return null;
          })()}
        </div>
        {/* Аллергены/диет-теги (§9.4). */}
        <div className="ta-field">
          <span className="hsc-input__label">Аллергены · тэги</span>
          <div className="menu-icon-picker" role="group" aria-label="Аллергены">
            {ALLERGEN_TAGS.map((t) => (
              <button
                type="button"
                key={t.key}
                className={`ta-chip mi-aller mi-aller--${t.tone}${
                  allergens.includes(t.key) ? ' is-on' : ''
                }`}
                aria-pressed={allergens.includes(t.key)}
                data-testid={`menu-item-allergen-${t.key}`}
                onClick={() => toggleAllergen(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="ta-field">
          <label htmlFor="mi-sort">Порядок сортировки</label>
          <input
            id="mi-sort"
            className="ta-input"
            type="number"
            value={form.sort_order}
            onChange={(e) => set('sort_order', Number(e.target.value) || 0)}
            data-testid="menu-item-sort"
          />
        </div>

        {/* Статус позиции «Активна» / архив (§9.3/§9.4, #74): снятый чекбокс
            убирает позицию из меню (deactivate), не удаляя её. */}
        <label className="menu-form__check">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid="menu-item-active"
          />
          Активна (показывать в меню)
        </label>
      </Card>

      <div className="menu-form__submit">
        <Button variant="primary" disabled={busy} onClick={submit} data-testid="menu-item-submit">
          {isEdit ? 'Сохранить' : 'Добавить позицию'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onClose} data-testid="menu-item-form-close">
          Назад
        </Button>
      </div>
    </section>
  );
}

export default MenuManager;
